import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { Destroyable, selectAllUsers, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { Observable } from 'rxjs'
import {
  ContractTemplate,
  SignatureRole,
  selectContractTemplatesForAutomations,
  selectContractTemplatesForAutomationsByType,
  selectContractTemplateViewById,
  TemplateTypes,
} from '@tokeet-frontend/signature'
import { AutomationHandler, AutomationHelpers, AutomationViewGroup } from '@tokeet-frontend/automations'
import * as lodash from 'lodash'
import { distinctUntilChanged, startWith, switchMap } from 'rxjs/operators'
import { EditTemplateDrawerService } from '@tv3/containers/documents/templates/template-drawer/edit-template/edit-template-drawer.service'
import { CreateTemplateDrawerService } from '../../../templates/template-drawer/create-template/create-template-drawer.service'
import { TemplateDrawerTab } from '@tv3/containers/documents/templates/template-drawer/edit-template/edit-template-drawer.component'

@Component({
  selector: 'app-automation-document-form',
  templateUrl: './automation-document-form.component.html',
  styleUrls: ['./automation-document-form.component.scss'],
})
export class AutomationDocumentFormComponent extends Destroyable implements OnInit, OnChanges {
  @Input() form: FormGroup
  @Input() handler: AutomationHandler
  @Input() handlerFromTemplate = false

  @Input() automation: AutomationViewGroup

  @Output() selectTemplate = new EventEmitter<ContractTemplate>()

  get templateCtrl() {
    return this.form.get('template') as FormControl
  }

  get signersCtrl() {
    return this.form.get('users') as FormArray
  }

  users$ = this.store.pipe(select(selectAllUsers))
  templates$: Observable<ContractTemplate[]>

  template: ContractTemplate = null

  constructor(
    private store: Store<any>,
    private editDialog: EditTemplateDrawerService,
    private addDialog: CreateTemplateDrawerService
  ) {
    super()
  }

  ngOnInit(): void {
    this.templateCtrl.valueChanges
      .pipe(
        startWith(this.automation?.template),
        switchMap((templateId) => this.store.pipe(select(selectContractTemplateViewById(templateId)))),
        distinctUntilChanged((a, b) => a?.template_id === b?.template_id),
        untilDestroy(this)
      )
      .subscribe((template) => {
        this.selectTemplate.emit(template)
        this.template = template
        this.setHandler(template)
        this.resetRecipients(template)
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.handler || changes.handlerFromTemplate) {
      this.refreshTemplates()
    }
  }

  onEdit() {
    this.editDialog.open(this.templateCtrl.value, TemplateDrawerTab.Details, false)
  }

  onAdd() {
    this.addDialog.open(this.handlerToDocumentType(this.handler))
  }

  setHandler(template: ContractTemplate) {
    if (this.handlerFromTemplate && template) {
      this.form.get('handler').setValue(this.documentTypeToHandler(template.type))
    }
  }

  refreshTemplates() {
    if (this.handler && !this.handlerFromTemplate) {
      this.templates$ = this.store.pipe(
        select(selectContractTemplatesForAutomationsByType(this.handlerToDocumentType(this.handler)))
      )
    } else if (this.handlerFromTemplate) {
      this.templates$ = this.store.pipe(select(selectContractTemplatesForAutomations))
    }
  }

  resetRecipients(template: ContractTemplate | undefined) {
    const guestCtrl = this.form.get('guest')
    guestCtrl.setValue(this.hasGuestRole(template?.roles))

    this.signersCtrl.clear()
    const userRoles = this.getUserRoles(template?.roles)
    const userIds = AutomationHelpers.withoutGuest(this.automation?.items || [])
    lodash.forEach(userRoles, (role, i) => {
      this.signersCtrl.push(new FormControl(userIds?.[i], [Validators.required]))
    })
  }

  getUserRoles(roles: string[]) {
    return lodash.filter(roles, (role) => SignatureRole.isUserRole(role))
  }

  hasUserRole(roles: string[]): boolean {
    return !!lodash.find(roles, (role) => SignatureRole.isUserRole(role))
  }

  hasGuestRole(roles: string[]): boolean {
    return !!lodash.find(roles, (role) => SignatureRole.isGuestRole(role))
  }

  handlerToDocumentType(handler: AutomationHandler) {
    switch (handler) {
      case AutomationHandler.SendContract:
        return TemplateTypes.Contract
      case AutomationHandler.SendDocument:
        return TemplateTypes.Document
      case AutomationHandler.SendForm:
        return TemplateTypes.Form
    }
  }

  documentTypeToHandler(type: TemplateTypes) {
    switch (type) {
      case TemplateTypes.Contract:
        return AutomationHandler.SendContract
      case TemplateTypes.Document:
        return AutomationHandler.SendDocument
      case TemplateTypes.Form:
        return AutomationHandler.SendForm
    }
  }
}
